/**
 * Valuation App Page
 *
 * Main application page after user authentication.
 * Contains the Wizard and Dashboard components.
 * Supports both legacy valuation and new VAC (Value Acceleration Calculator).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Wizard from '../components/Wizard';
import Dashboard from '../components/Dashboard';
import { VACWizard, VACDashboard } from '../components/vac';
import { AdvisorDashboard, OverridePanel } from '../components/advisor';
import { MVADashboard } from '../components/mva';
import '../App.css';

// Simple unique ID generator
const generateId = () => `val_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

function ValuationApp() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Mode: 'vac' for Value Acceleration Calculator, 'legacy' for old system
  const [appMode, setAppMode] = useState('vac');

  // View: what's currently being displayed
  const [currentView, setCurrentView] = useState('wizard'); // 'wizard', 'dashboard', 'advisor', 'override'

  const [userId] = useState(user?.id);
  const [valuationId, setValuationId] = useState(() => generateId());
  const [valuation, setValuation] = useState(null);
  const [vacResult, setVacResult] = useState(null);
  const [mvaResult, setMvaResult] = useState(null);

  // Legacy wizard complete handler
  const handleWizardComplete = (valuationData, newValuationId) => {
    setValuation(valuationData);
    setValuationId(newValuationId);
    setCurrentView('dashboard');
  };

  // VAC wizard complete handler
  const handleVACComplete = (result, vId) => {
    setVacResult(result);
    setValuationId(vId);
    setCurrentView('dashboard');
  };

  const handleReturnToWizard = () => {
    setCurrentView('wizard');
  };

  const handleStartNewAssessment = () => {
    setValuationId(generateId());
    setVacResult(null);
    setMvaResult(null);
    setCurrentView('wizard');
  };

  // Navigate to MVA deep analysis
  const handleGoToMVA = () => {
    setCurrentView('mva');
  };

  // MVA calculation complete handler
  const handleMVAComplete = (result) => {
    setMvaResult(result);
  };

  const handleValuationUpdate = (updatedValuation) => {
    setValuation(updatedValuation);
  };

  const handleSelectValuation = (vId, mode = 'view') => {
    setValuationId(vId);
    if (mode === 'edit') {
      setCurrentView('override');
    } else {
      setCurrentView('dashboard');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Determine user type based on role (can be expanded)
  const userType = user?.role === 'advisor' ? 'advisor' : 'owner';

  // Render content based on current view
  const renderContent = () => {
    // VAC Mode
    if (appMode === 'vac') {
      switch (currentView) {
        case 'wizard':
          return (
            <VACWizard
              valuationId={valuationId}
              userId={userId}
              userType={userType}
              onComplete={handleVACComplete}
            />
          );

        case 'dashboard':
          return (
            <VACDashboard
              result={vacResult}
              onRecalculate={handleReturnToWizard}
              onDeepAnalysis={handleGoToMVA}
            />
          );

        case 'mva':
          return (
            <MVADashboard
              result={mvaResult}
              vacResult={vacResult}
              valuationId={valuationId}
              userId={userId}
              onRecalculate={handleReturnToWizard}
              onMVAComplete={handleMVAComplete}
              onBack={() => setCurrentView('dashboard')}
            />
          );

        case 'advisor':
          return (
            <AdvisorDashboard
              userId={userId}
              onSelectValuation={handleSelectValuation}
              onCreateNew={handleStartNewAssessment}
            />
          );

        case 'override':
          return (
            <OverridePanel
              valuationId={valuationId}
              onSave={() => setCurrentView('dashboard')}
            />
          );

        default:
          return null;
      }
    }

    // Legacy Mode
    if (currentView === 'wizard') {
      return (
        <Wizard
          userId={userId}
          onComplete={handleWizardComplete}
        />
      );
    }

    return (
      <Dashboard
        valuation={valuation}
        valuationId={valuationId}
        onReturnToWizard={handleReturnToWizard}
        onValuationUpdate={handleValuationUpdate}
      />
    );
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1>Business Valuation & Exit Planning</h1>
              <p className="subtitle">Value Acceleration Calculator</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Mode Toggle */}
              <div className="mode-toggle">
                <button
                  className={`mode-btn ${appMode === 'vac' ? 'active' : ''}`}
                  onClick={() => setAppMode('vac')}
                >
                  VAC
                </button>
                <button
                  className={`mode-btn ${appMode === 'legacy' ? 'active' : ''}`}
                  onClick={() => setAppMode('legacy')}
                >
                  Legacy
                </button>
              </div>

              {/* Nav Links for VAC Mode */}
              {appMode === 'vac' && (
                <nav className="app-nav">
                  <button
                    className={`nav-btn ${currentView === 'wizard' ? 'active' : ''}`}
                    onClick={handleStartNewAssessment}
                  >
                    New Assessment
                  </button>
                  {userType === 'advisor' && (
                    <button
                      className={`nav-btn ${currentView === 'advisor' ? 'active' : ''}`}
                      onClick={() => setCurrentView('advisor')}
                    >
                      Engagements
                    </button>
                  )}
                </nav>
              )}

              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>
                  Welcome, <strong>{user?.fullName}</strong>
                </p>
                <button
                  onClick={handleLogout}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    borderRadius: '6px',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.2)';
                  }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="container">
          {renderContent()}
        </div>
      </main>

      <footer className="app-footer">
        <p>&copy; 2026 Exit Ready. All valuations are estimates.</p>
      </footer>
    </div>
  );
}

export default ValuationApp;
